import { useMemo, useState } from "react";
import type { DemandAnalysis } from "../../api/types";
import {
  DIRS,
  type Dir,
  fmtRiders,
  niceScale,
  stationAbbr,
  stationLabel,
} from "./demandMath";
import { useElementWidth } from "./useElementWidth";

interface Props {
  demand: DemandAnalysis;
  /** Riders aboard on each link within the selected time window, by direction. */
  loads: Record<Dir, number[]>;
  dirNames: Record<Dir, string>;
  windowLabel: string;
}

const MARGIN = { left: 46, right: 14, top: 22, bottom: 34 };
const PLOT_H = 226;
const HEIGHT = MARGIN.top + PLOT_H + MARGIN.bottom;
const TIP_W = 260; // matches .dm-tip

function barPath(x: number, y: number, w: number, h: number): string {
  if (h <= 0 || w <= 0) return "";
  const r = Math.min(4, w / 2, h);
  return (
    `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} ` +
    `L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`
  );
}

/**
 * Riders aboard between each pair of consecutive stops: the route laid out
 * left to right, with a pair of bars on every link. The tallest bar in a
 * direction is its peak load point, the figure a headway is sized from.
 */
export default function LoadProfile({ demand, loads, dirNames, windowLabel }: Props) {
  const [wrapRef, width] = useElementWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);

  const stops = demand.stops;
  const links = Math.max(0, stops.length - 1);

  const scale = useMemo(
    () => niceScale(Math.max(0, ...loads.upward, ...loads.downward)),
    [loads]
  );
  const peakLink = useMemo(() => {
    const at = (values: number[]) => {
      let best = -1;
      values.forEach((v, k) => {
        if (v > 0 && (best < 0 || v > values[best])) best = k;
      });
      return best;
    };
    return { upward: at(loads.upward), downward: at(loads.downward) };
  }, [loads]);

  const plotW = Math.max(0, width - MARGIN.left - MARGIN.right);
  const linkW = links > 0 ? plotW / links : 0;
  const stopX = (i: number) => MARGIN.left + i * linkW;
  const barW = Math.min(24, Math.max(3, (linkW - 14) / 2));
  const baseline = MARGIN.top + PLOT_H;
  const y = (v: number) => baseline - (Math.min(v, scale.max) / scale.max) * PLOT_H;

  const ticks: number[] = [];
  for (let v = 0; v <= scale.max + 1e-9; v += scale.step) ticks.push(v);

  // Full names when a link is wide enough to hold one, initials otherwise.
  const label = (name: string) => {
    const full = stationLabel(name);
    return full.length * 6.4 <= linkW - 10 ? full : stationAbbr(name);
  };
  const linkName = (k: number) => `${stationLabel(stops[k].name)} – ${stationLabel(stops[k + 1].name)}`;

  return (
    <div className="dm-chart">
      <div className="dm-chart-head">
        <div>
          <h4>Riders aboard along the route</h4>
          <p className="dm-chart-sub">Riders per day on each stretch between stops · {windowLabel}</p>
        </div>
        <div className="sched-segmented dm-view-toggle" role="group" aria-label="View as">
          <button type="button" className={!asTable ? "active" : ""} onClick={() => setAsTable(false)}>Chart</button>
          <button type="button" className={asTable ? "active" : ""} onClick={() => setAsTable(true)}>Table</button>
        </div>
      </div>

      {asTable ? (
        <div className="dm-table-scroll">
          <table className="data-table compact dm-table">
            <thead>
              <tr>
                <th>Between</th>
                {DIRS.map((dir) => (
                  <th key={dir} className="num">
                    <span className={`dm-swatch dm-swatch-${dir}`} aria-hidden /> {dirNames[dir]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: links }, (_, k) => (
                <tr key={k}>
                  <td>{linkName(k)}</td>
                  {DIRS.map((dir) => (
                    <td key={dir} className="num">
                      {fmtRiders(loads[dir][k] ?? 0)}
                      {k === peakLink[dir] ? <span className="dm-peak-tag">peak</span> : null}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="dm-fit-wrap" ref={wrapRef}>
          {width > 0 && links > 0 && (
            <div className="dm-fit-panel">
              <svg width={width} height={HEIGHT} role="img"
                aria-label={`Riders aboard on each stretch of the route, ${windowLabel}`}>
                {hover != null && (
                  <rect className="dm-hover-band" x={stopX(hover)} y={MARGIN.top - 8} width={linkW} height={PLOT_H + 8} />
                )}
                {ticks.map((v) => (
                  <g key={v}>
                    <line className={v === 0 ? "dm-axis" : "dm-gridline"}
                      x1={MARGIN.left} x2={MARGIN.left + plotW} y1={y(v)} y2={y(v)} />
                    <text className="dm-tick" x={MARGIN.left - 8} y={y(v)} dy="0.32em" textAnchor="end">
                      {fmtRiders(v)}
                    </text>
                  </g>
                ))}

                {Array.from({ length: links }, (_, k) => {
                  const centre = stopX(k) + linkW / 2;
                  return (
                    <g key={k}>
                      {DIRS.map((dir, j) => {
                        const v = loads[dir][k] ?? 0;
                        const top = y(v);
                        // 2px of surface between the pair: the gap separates them, not a stroke.
                        const x = j === 0 ? centre - 1 - barW : centre + 1;
                        return (
                          <g key={dir}>
                            <path className={`dm-bar dm-bar-${dir}`} d={barPath(x, top, barW, baseline - top)} />
                            {k === peakLink[dir] && (
                              // Anchored away from the pair's centre line: both
                              // directions often peak on the same link, and two
                              // centred labels would run into each other.
                              <text className="dm-bar-label" y={top - 6}
                                x={j === 0 ? centre - 3 : centre + 3} textAnchor={j === 0 ? "end" : "start"}>
                                {fmtRiders(v)}
                              </text>
                            )}
                          </g>
                        );
                      })}
                    </g>
                  );
                })}

                {stops.map((s, i) => (
                  <g key={s.stop_id}>
                    <line className="dm-axis" x1={stopX(i)} x2={stopX(i)} y1={baseline} y2={baseline + 5} />
                    <text className="dm-tick dm-stop-tick" x={stopX(i)} y={baseline + 19}
                      textAnchor={i === 0 ? "start" : i === stops.length - 1 ? "end" : "middle"}>
                      <title>{s.name}</title>
                      {label(s.name)}
                    </text>
                  </g>
                ))}

                {Array.from({ length: links }, (_, k) => (
                  <rect key={k} className="dm-hit"
                    x={stopX(k)} y={MARGIN.top - 8} width={linkW} height={PLOT_H + 8}
                    tabIndex={0}
                    aria-label={`${linkName(k)}: ${DIRS.map((d) => `${fmtRiders(loads[d][k] ?? 0)} aboard ${dirNames[d]}`).join(", ")}`}
                    onPointerEnter={() => setHover(k)}
                    onPointerLeave={() => setHover((h) => (h === k ? null : h))}
                    onFocus={() => setHover(k)}
                    onBlur={() => setHover(null)}
                  />
                ))}
              </svg>

              {hover != null && (
                <div className="dm-tip" role="status"
                  style={{
                    // Beside the hovered stretch, never over it.
                    left: stopX(hover + 1) + 10 + TIP_W <= width
                      ? stopX(hover + 1) + 10
                      : Math.max(0, stopX(hover) - 10 - TIP_W),
                    top: MARGIN.top,
                  }}>
                  <div className="dm-tip-title">{linkName(hover)}</div>
                  {DIRS.map((dir) => (
                    <div className="dm-tip-row" key={dir}>
                      <span className={`dm-linekey dm-linekey-${dir}`} aria-hidden />
                      <div>
                        <strong>{fmtRiders(loads[dir][hover] ?? 0)}</strong> aboard {dirNames[dir]}
                        {hover === peakLink[dir] ? <span className="dm-tip-detail"> · peak load point</span> : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="dm-legend">
            {DIRS.map((dir) => (
              <span className="dm-legend-item" key={dir}>
                <span className={`dm-swatch dm-swatch-${dir}`} aria-hidden /> Aboard {dirNames[dir]}
              </span>
            ))}
            <span className="dm-legend-hint">The labelled bar is each direction's peak load point.</span>
          </div>
        </div>
      )}
    </div>
  );
}
